import { loadGalleryConfig, type GalleryConfig, type MediaItem } from './gallery';

const CONFIG_PROMISES = new Map<string, Promise<GalleryConfig>>();
const PREPARE_PROMISES = new Map<string, Promise<GalleryConfig>>();
const ASSET_PROMISES = new Map<string, Promise<void>>();

const PRELOAD_CONCURRENCY = 6;
const IMAGE_RETRY_COUNT = 2;
const VIDEO_RETRY_COUNT = 1;
const VIDEO_READY_TIMEOUT_MS = 20_000;

type AssetDescriptor =
  | { kind: 'image'; src: string }
  | { kind: 'video'; src: string };

const uniqueAssetsForConfig = (config: GalleryConfig): AssetDescriptor[] => {
  const seen = new Set<string>();
  const assets: AssetDescriptor[] = [];

  const pushUnique = (asset: AssetDescriptor) => {
    const key = `${asset.kind}:${asset.src}`;
    if (seen.has(key)) return;
    seen.add(key);
    assets.push(asset);
  };

  config.media.forEach((item) => {
    if (item.type === 'video') {
      pushUnique({ kind: 'video', src: item.src });
      return;
    }
    if (item.type === 'pair') {
      pushUnique({ kind: 'image', src: item.leftSrc });
      pushUnique({ kind: 'image', src: item.rightSrc });
      return;
    }
    pushUnique({ kind: 'image', src: item.src });
  });

  return assets;
};

const loadGalleryConfigCached = (configPath: string): Promise<GalleryConfig> => {
  const cached = CONFIG_PROMISES.get(configPath);
  if (cached) return cached;
  const promise = loadGalleryConfig(configPath).catch((error) => {
    CONFIG_PROMISES.delete(configPath);
    throw error;
  });
  CONFIG_PROMISES.set(configPath, promise);
  return promise;
};

const preloadImage = (src: string, retriesLeft = IMAGE_RETRY_COUNT): Promise<void> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const cleanup = () => {
      image.onload = null;
      image.onerror = null;
    };

    image.onload = () => {
      const finish = async () => {
        try {
          if (typeof image.decode === 'function') {
            await image.decode();
          }
        } catch {
          // ignore decode failures after a successful load
        } finally {
          cleanup();
          resolve();
        }
      };
      void finish();
    };

    image.onerror = () => {
      cleanup();
      if (retriesLeft > 0) {
        void preloadImage(src, retriesLeft - 1).then(resolve).catch(reject);
        return;
      }
      reject(new Error(`图片素材预加载失败：${src}`));
    };

    image.src = src;
    if (image.complete && image.naturalWidth > 0) {
      image.onload?.(new Event('load'));
    }
  });

const preloadVideo = (src: string, retriesLeft = VIDEO_RETRY_COUNT): Promise<void> =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    let settled = false;
    const timeoutId = window.setTimeout(() => {
      cleanup();
      if (retriesLeft > 0) {
        void preloadVideo(src, retriesLeft - 1).then(resolve).catch(reject);
        return;
      }
      reject(new Error(`视频素材预加载超时：${src}`));
    }, VIDEO_READY_TIMEOUT_MS);

    const cleanup = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      video.removeEventListener('loadeddata', handleReady);
      video.removeEventListener('canplay', handleReady);
      video.removeEventListener('error', handleError);
      video.pause();
      video.removeAttribute('src');
      video.load();
    };

    const handleReady = () => {
      cleanup();
      resolve();
    };

    const handleError = () => {
      cleanup();
      if (retriesLeft > 0) {
        void preloadVideo(src, retriesLeft - 1).then(resolve).catch(reject);
        return;
      }
      reject(new Error(`视频素材预加载失败：${src}`));
    };

    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.addEventListener('loadeddata', handleReady, { once: true });
    video.addEventListener('canplay', handleReady, { once: true });
    video.addEventListener('error', handleError, { once: true });
    video.src = src;
    video.load();
  });

const preloadAsset = (asset: AssetDescriptor): Promise<void> => {
  const key = `${asset.kind}:${asset.src}`;
  const cached = ASSET_PROMISES.get(key);
  if (cached) return cached;

  const promise = asset.kind === 'video' ? preloadVideo(asset.src) : preloadImage(asset.src);
  ASSET_PROMISES.set(
    key,
    promise.catch((error) => {
      ASSET_PROMISES.delete(key);
      throw error;
    }),
  );
  return ASSET_PROMISES.get(key)!;
};

const runWithConcurrency = async <T>(items: T[], limit: number, worker: (item: T) => Promise<void>) => {
  let cursor = 0;
  const threads = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const current = items[cursor];
      cursor += 1;
      await worker(current);
    }
  });
  await Promise.all(threads);
};

export const prepareGalleryStimuli = (configPath: string): Promise<GalleryConfig> => {
  const cached = PREPARE_PROMISES.get(configPath);
  if (cached) return cached;

  const promise = loadGalleryConfigCached(configPath)
    .then(async (config) => {
      const assets = uniqueAssetsForConfig(config);
      if (assets.length > 0) {
        await runWithConcurrency(assets, PRELOAD_CONCURRENCY, preloadAsset);
      }
      return config;
    })
    .catch((error) => {
      PREPARE_PROMISES.delete(configPath);
      throw error;
    });

  PREPARE_PROMISES.set(configPath, promise);
  return promise;
};

export const preloadMediaItem = async (item: MediaItem): Promise<void> => {
  if (item.type === 'video') {
    await preloadAsset({ kind: 'video', src: item.src });
    return;
  }
  if (item.type === 'pair') {
    await Promise.all([
      preloadAsset({ kind: 'image', src: item.leftSrc }),
      preloadAsset({ kind: 'image', src: item.rightSrc }),
    ]);
    return;
  }
  await preloadAsset({ kind: 'image', src: item.src });
};
